import type {
  PromptCitations,
  PromptDetail,
  PromptHistory,
  PromptResponses,
  PromptTable,
  PromptTrend,
} from "@crux/contracts";
import { api } from "../client.js";
import { problem } from "./errors.js";

/**
 * One question on a report, and the four panels behind it.
 */

export async function fetchPromptTable(
  workspaceId: string,
  reportId: string,
  query: {
    readonly from: string;
    readonly to: string;
    /** Omitted for every engine — the API has no wildcard value. */
    readonly engine?: string;
  },
): Promise<PromptTable> {
  const { data, response } = await api.GET(
    "/api/workspaces/{workspaceId}/reports/{reportId}/prompt-table",
    { params: { path: { workspaceId, reportId }, query } },
  );
  if (data === undefined) {
    throw new Error(problem(response.status, "load these questions"));
  }
  return data;
}

/** Each question's answers day by day, and its last capture on each engine. */
export async function fetchPromptTrend(
  workspaceId: string,
  reportId: string,
  query: {
    readonly from: string;
    readonly to: string;
    readonly engine?: string;
  },
): Promise<PromptTrend> {
  const { data, response } = await api.GET(
    "/api/workspaces/{workspaceId}/reports/{reportId}/prompt-trend",
    { params: { path: { workspaceId, reportId }, query } },
  );
  if (data === undefined) {
    throw new Error(problem(response.status, "load how these questions moved"));
  }
  return data;
}

export async function fetchPromptDetail(
  workspaceId: string,
  reportId: string,
  promptId: string,
  query: {
    readonly from: string;
    readonly to: string;
    readonly engine?: string;
  },
): Promise<PromptDetail> {
  const { data, response } = await api.GET(
    "/api/workspaces/{workspaceId}/reports/{reportId}/prompts/{promptId}",
    { params: { path: { workspaceId, reportId, promptId }, query } },
  );
  if (data === undefined) {
    throw new Error(problem(response.status, "open this question"));
  }
  return data;
}

export async function fetchPromptResponses(
  workspaceId: string,
  reportId: string,
  promptId: string,
  query: {
    readonly from: string;
    readonly to: string;
    readonly engine?: string;
  },
): Promise<PromptResponses> {
  const { data, response } = await api.GET(
    "/api/workspaces/{workspaceId}/reports/{reportId}/prompts/{promptId}/responses",
    { params: { path: { workspaceId, reportId, promptId }, query } },
  );
  if (data === undefined) {
    throw new Error(problem(response.status, "load these answers"));
  }
  return data;
}

export async function fetchPromptCitations(
  workspaceId: string,
  reportId: string,
  promptId: string,
  query: {
    readonly from: string;
    readonly to: string;
    readonly engine?: string;
  },
): Promise<PromptCitations> {
  const { data, response } = await api.GET(
    "/api/workspaces/{workspaceId}/reports/{reportId}/prompts/{promptId}/citations",
    { params: { path: { workspaceId, reportId, promptId }, query } },
  );
  if (data === undefined) {
    throw new Error(problem(response.status, "load these citations"));
  }
  return data;
}

/** One question's captures, one per engine per day: the grid on its page. */
export async function fetchPromptHistory(
  workspaceId: string,
  reportId: string,
  promptId: string,
  query: {
    readonly from: string;
    readonly to: string;
    readonly engine?: string;
  },
): Promise<PromptHistory> {
  const { data, response } = await api.GET(
    "/api/workspaces/{workspaceId}/reports/{reportId}/prompts/{promptId}/history",
    { params: { path: { workspaceId, reportId, promptId }, query } },
  );
  if (data === undefined) {
    throw new Error(problem(response.status, "load this question's days"));
  }
  return data;
}
